import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./logo";
import { NAV_LINKS, CTAS, whatsappHref, SITE } from "@/content/site";

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`sticky top-0 z-50 border-b backdrop-blur-md transition-colors duration-300 ${
          scrolled ? "bg-parchment/90 border-hairline/60" : "bg-parchment/70 border-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-8">
          <Link to="/" aria-label="Tulevik Legal — inicio" data-testid="header-logo-link">
            <Logo tam="lg" />
          </Link>

          <nav className="hidden items-center gap-8 md:flex" aria-label="Navegación principal">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.href}
                to={l.href}
                data-testid={`nav-link-${l.href.replace(/\//g, "") || "inicio"}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors duration-200 hover:text-azul ${
                    isActive ? "text-azul" : "text-ink-text"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to={CTAS.principal.href}
              data-testid="header-cta-principal"
              className="hidden rounded-md bg-azul px-5 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:bg-azul-dark active:scale-[0.98] sm:inline-flex"
            >
              {CTAS.principal.label}
            </Link>
            <button
              type="button"
              onClick={() => setOpen(!open)}
              data-testid="header-menu-toggle"
              aria-label={open ? "Cerrar menú" : "Abrir menú"}
              aria-expanded={open}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-hairline text-ink-deep transition-colors hover:bg-parchment-surface md:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div
          className="fixed inset-0 z-40 flex flex-col bg-parchment px-6 pb-10 pt-24 md:hidden"
          data-testid="mobile-menu-overlay"
        >
          <nav className="flex flex-col" aria-label="Navegación móvil">
            {NAV_LINKS.map((l, i) => (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`mobile-nav-link-${l.href.replace(/\//g, "") || "inicio"}`}
                className="border-b border-hairline py-4 font-serif text-3xl text-ink-deep transition-colors hover:text-azul"
              >
                <span className="mr-3 font-mono text-xs text-azul">0{i + 1}</span>
                {l.label}
              </Link>
            ))}
            <Link
              to="/orientador"
              data-testid="mobile-nav-link-orientador"
              className="border-b border-hairline py-4 font-serif text-3xl text-ink-deep transition-colors hover:text-azul"
            >
              <span className="mr-3 font-mono text-xs text-azul">05</span>
              Encuentra tu ruta
            </Link>
            <Link
              to="/atencion-prioritaria"
              data-testid="mobile-nav-link-prioritaria"
              className="border-b border-hairline py-4 font-serif text-3xl text-azul transition-colors hover:text-azul-dark"
            >
              <span className="mr-3 font-mono text-xs text-azul">06</span>
              Atención prioritaria
            </Link>
          </nav>

          <div className="mt-auto flex flex-col gap-3 pt-8">
            <Link
              to="/contacto"
              data-testid="mobile-menu-cta-principal"
              className="btn-primary w-full"
            >
              {CTAS.principal.label}
            </Link>
            <a
              href={whatsappHref()}
              data-testid="mobile-menu-cta-whatsapp"
              className="btn-secondary w-full"
              {...(SITE.WHATSAPP_NUMERO ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            >
              {CTAS.secundaria.label}
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </>
  );
}
